import type { QueryClient, QueryKey } from '@tanstack/react-query';

type BillingError = {
  status: number;
  code?: string;
  details?: Record<string, unknown>;
};
let handler: ((error: BillingError) => void) | undefined;

export function registerBillingRecoveryHandler(
  next: (error: BillingError) => void
) {
  handler = next;
}

export function handleBillingError(error: BillingError): void {
  if (error.status === 403 && error.code === 'BILLING_ACTION_REQUIRED')
    handler?.(error);
}

// A page that loads ten things fires ten requests at once; on a billing
// denial all ten come back 403 together. One re-check of the subscription
// answers them all.
const BILLING_RECHECK_MIN_GAP_MS = 5_000;

/**
 * Re-check the subscription after a billing denial — once per burst.
 *
 * Invalidating on every 403 cancelled and restarted the subscription fetch
 * each time, and the API still served every cancelled request: 2,045
 * subscription calls in 10 seconds from one tab (2026-10-09). Skip while a
 * check is in flight or one finished in the last few seconds, and never
 * cancel one that is running.
 */
export function recheckSubscriptionAfterBillingError(
  queryClient: QueryClient,
  queryKey: QueryKey,
  now: number = Date.now()
): void {
  const state = queryClient.getQueryState(queryKey);
  if (state?.fetchStatus === 'fetching') return;
  // A check that FAILED is just as recent: dataUpdatedAt only moves on
  // success, so with the subscription endpoint down every 403 would re-check.
  const lastCheckedAt = Math.max(
    state?.dataUpdatedAt ?? 0,
    state?.errorUpdatedAt ?? 0
  );
  if (now - lastCheckedAt < BILLING_RECHECK_MIN_GAP_MS) return;
  void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
}
